import { genres } from './data/genres';

/* What the shelf can be filtered and sorted by.

   Every choice is offered only when the data can answer it: "Up next" needs a
   book marked `want`, a rating sort needs a rating. So the menus grow on their own as the book files fill in, and
   nothing is ever offered that would do nothing. */

// Each label is the phrase that sits after "Showing" in the sentence.
const STATUSES = [
  { id: 'all', label: 'all books' },
  { id: 'reading', label: 'books I\u2019m reading' },
  { id: 'finished', label: 'finished books' },
  { id: 'want', label: 'books up next' },
];

const SORTS = [
  { id: 'shelf', label: 'in shelf order', needs: null },
  { id: 'rating', label: 'best rated first', needs: book => book.rating != null },
  // Always offered: undated books simply keep their shelf order at the end.
  { id: 'recent', label: 'most recent first', needs: null },
  { id: 'title', label: 'by title', needs: null },
];

// Opens on what has been read; the reading list is one choice away.
export const DEFAULT_VIEW = { status: 'finished', genre: 'all', sort: 'shelf' };

export function matches(book, view) {
  return (
    (view.status === 'all' || book.status === view.status) &&
    (view.genre === 'all' || book.genre === view.genre)
  );
}

/* The hand-written running order breaks every tie, so a sort never shuffles
   books it has nothing to say about. */
/* When a book was last read: finished if it was, otherwise started. */
const readOn = book => book.finishedOn || book.startedOn || '';

export function applyView(books, view) {
  const rank = new Map(books.map((book, i) => [book.slug, i]));
  const byShelf = (a, b) => rank.get(a.slug) - rank.get(b.slug);
  const compare = {
    shelf: byShelf,
    rating: (a, b) => (b.rating ?? -1) - (a.rating ?? -1) || byShelf(a, b),
    recent: (a, b) => readOn(b).localeCompare(readOn(a)) || byShelf(a, b),
    title: (a, b) => a.title.localeCompare(b.title, 'en'),
  }[view.sort];
  return books.filter(book => matches(book, view)).sort(compare);
}

export function options(books, view) {
  const count = patch => books.filter(book => matches(book, { ...view, ...patch })).length;
  return {
    status: STATUSES.filter(s => s.id === 'all' || books.some(b => b.status === s.id)).map(s => ({
      ...s,
      count: count({ status: s.id }),
    })),
    genre: [
      { id: 'all', label: 'every genre', count: count({ genre: 'all' }) },
      ...genres
        .filter(g => books.some(b => b.genre === g.id))
        .map(g => ({ id: g.id, label: g.label.toLowerCase(), count: count({ genre: g.id }) })),
    ],
    sort: SORTS.filter(s => !s.needs || books.some(s.needs)),
  };
}
